import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { tokens } from "../../theme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  Box,
  Chip,
} from "@mui/material";
import {
  getUserError,
  getUserStatus,
  updateStatus,
  selectAllUsers,
  getUserSuccess,
  getAllUsers,
  deleteUser,
  blockUser,
} from "../../features/userSlice";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import AlertDialog from "../../components/alertDialog";
import { toast } from "react-toastify";
import TableErrorMessage from "../../components/noDataTable";
import {
  BlockList,
  getUserRoleLabel,
  UserRole,
  UserRoleList,
} from "../../models/user.model";
import AddIcon from "@mui/icons-material/Add";
import MenuItemFilter from "../../components/filters/menuItemFilter";
import AppBarSearch from "../../global/appBarSearch";
import ParagraphHighliter from "../../components/paragraphHighliter";
import { ResponsiveProvider } from "../../hooks/uiHook";
import RestaurantTopBar from "../../components/restaurantTopBar";
import RestaurantPermissions from "../../components/restaurantPermissions";
import { useTranslation } from "react-i18next";
import TableActions from "../../components/tableActions";
const UserPermissions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [localUsers, setLocalUsers] = useState([]);
  const [userStateFilter, setUserStateFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [search, setSearch] = useState("");
  const [openBlock, setOpenBlock] = useState(false);
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const status = useSelector(getUserStatus);
  const error = useSelector(getUserError);
  const users = useSelector(selectAllUsers);
  const success = useSelector(getUserSuccess);
  const [openRestaurantModal, setOpenRestaurantModal] = useState(false);

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { user: data } });
  };

  useEffect(() => {
    if (status === "fetchAllUsersSuccess") setLocalUsers(users);
  }, [users, status]);

  useEffect(() => {
    const alreadyLoaded = users.length > 0 && status === "fetchAllUsersSuccess";
    if (!alreadyLoaded) {
      dispatch(getAllUsers());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const filteredUsersMemo = useMemo(() => {
    let filtered =
      localUsers?.filter((user) => user.role !== UserRole.admin) || [];

    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(lowerSearch) ||
          user.email?.toLowerCase().includes(lowerSearch)
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    if (userStateFilter !== "") {
      filtered = filtered.filter(
        (user) => String(user.isBlocked) === String(userStateFilter)
      );
    }

    return filtered;
  }, [localUsers, search, roleFilter, userStateFilter]);

  useEffect(() => {
    if (status === "blockSuccess") {
      toast.success(success);
      setLocalUsers((prev) =>
        prev.map((item) =>
          item._id === cardId ? { ...item, isBlocked: !item.isBlocked } : item
        )
      );
      dispatch(updateStatus());
    } else if (status === "blockError") {
      toast.error(error);
    }
  }, [status, success, error, dispatch, cardId]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      setLocalUsers((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [status, error, success, dispatch, cardId]);

  return (
    <div className="main-application">
      <RestaurantTopBar />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          searchText={t("search.byNameOrEmail")}
          handleSearch={(e) => setSearch(e.target.value)}
          title={t("pages.myUsers")}
          children={
            <>
              <MenuItemFilter
                label={t("filters.allRoles")}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                filters={UserRoleList}
              />
              <MenuItemFilter
                label={t("filters.allStates")}
                value={userStateFilter}
                onChange={(e) => setUserStateFilter(e.target.value)}
                filters={BlockList}
              />
            </>
          }
          actions={
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleSubmit}
            >
              {t("actions.addUser")}
            </Button>
          }
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          <TableContainer>
            <Table
              sx={{
                backgroundColor: colors.primary[400],
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: colors.primary[700],
                }}
              >
                <TableRow>
                  <TableCell>{t("table.fullName")}</TableCell>
                  <TableCell>{t("table.email")}</TableCell>
                  <TableCell>{t("table.role")}</TableCell>
                  <TableCell>{t("table.status")}</TableCell>
                  <TableCell align="right">{t("table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status === "loadingFetchAllUsers" ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Loading size={8} />
                    </TableCell>
                  </TableRow>
                ) : status === "fetchAllUsersError" ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Error>{error}</Error>
                    </TableCell>
                  </TableRow>
                ) : filteredUsersMemo && filteredUsersMemo.length > 0 ? (
                  filteredUsersMemo.map((card) => (
                    <TableRow key={card._id}>
                      <TableCell>
                        <ParagraphHighliter
                          title={card?.fullName}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>
                        <ParagraphHighliter
                          title={card?.email}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>{getUserRoleLabel(card.role)}</TableCell>
                      <TableCell>
                        <Chip
                          variant="outlined"
                          label={card.isBlocked ? t("common.blocked") : t("common.unblocked")}
                          color={card.isBlocked ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TableActions
                          actions={[
                            {
                              type: "activate",
                              color: "info",
                              label: t("actions.activateNotifications"),
                              onClick: () => {
                                setSelectedUserName(card.fullName);
                                setCardId(card._id);
                                setOpenRestaurantModal(true);
                              },
                            },
                            {
                              type: card.isBlocked ? "unblock" : "block",
                              color: "primary",
                              label: card.isBlocked
                                ? t("actions.unblockUser")
                                : t("actions.blockUser"),
                              onClick: () => {
                                setIsBlocked(!card.isBlocked);
                                setOpenBlock(true);
                                setCardId(card._id);
                                setSelectedUserName(card.fullName);
                              },
                            },
                            {
                              type: "delete",
                              color: "error",
                              label: t("common.delete"),
                              onClick: () => handleClickOpen(card._id),
                            },
                            {
                              type: "edit",
                              color: "warning",
                              label: t("common.edit"),
                              onClick: () => handleModify(card),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableErrorMessage cols={5} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </main>
      <AlertDialog
        handleClose={() => setOpen(false)}
        open={open}
        message="Voulez-vous supprimer cet utilisateur ?"
        cardId={cardId}
        deleteData={() =>
          dispatch(deleteUser({ userId: cardId }))
        }
      />
      <AlertDialog
        handleClose={() => setOpenBlock(false)}
        open={openBlock}
        title={isBlocked ? t("actions.blockUser") : t("actions.unblockUser")}
        message={`Voulez-vous ${isBlocked ? "bloquer" : "débloquer"} l'utilisateur ${selectedUserName} ?`}
        cardId={cardId}
        deleteData={() => {
          dispatch(
            blockUser({
              userId: cardId
            })
          );
          setIsBlocked(false);
        }}
      />
      {openRestaurantModal && <RestaurantPermissions
        open={openRestaurantModal}
        handleClose={() => setOpenRestaurantModal(false)}
        userName={selectedUserName}
        userId={cardId}
      />}
    </div>
  );
};

export default UserPermissions;
